import { TooltipStyled } from '@OnArk/styles/Layout.styled';

import React, { ReactElement } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { MaxWidthStyled, mq } from '@hangar31/rc-layout';
import { Locale } from '@lang/getLocale';
import toColor, { ColorInput } from '@OnArk/utils/toColor';

import LangPicker from './LangPicker';

const ProgressBarStyled = styled('a', {
  shouldForwardProp: prop => prop !== 'complete' && prop !== 'barColor',
})<{ complete: boolean; barColor?: ColorInput }>(
  ({ theme }) => css`
    display: block;
    height: 1rem;
    margin: 0 0.4rem;

    background-color: ${theme.colors.disabled};
    border-radius: ${theme.radius.md};

    pointer-events: none;
  `,
  ({ barColor, complete, theme }) =>
    complete &&
    css`
      background-color: ${toColor(barColor ?? theme.church.buttonColor)};

      pointer-events: all;
    `
);

function GuestNav({
  church,
  course,
  currentSection,
  locale,
  ...props
}: {
  locale: Locale;
  church: {
    logo?: string | null;
    url?: string;
    buttonColor?: unknown;
    name?: string;
  };
  course?: {
    url?: string | null;
    name?: string | null;
    sections: {
      parentId?: string | null;
      name: string;
      id: string;
      order?: number | null;
    }[];
  };
  currentSection: { order?: number | null };
}): ReactElement {
  return (
    <>
      <Global
        styles={theme => css`
          body {
            background: ${theme.colors.white};
          }
        `}
      />
      <LangPicker locale={locale} />
      <MaxWidthStyled
        grid
        align="center"
        bottom={5}
        padding={3}
        rowGap={0}
        size="sm"
        {...props}
      >
        <i
          css={mq({
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            margin: '0 auto',
            maxWidth: ['100%', undefined, '29rem'],
            minHeight: ['4.2rem', undefined, '10rem'],
            width: church.logo ? ['12.2rem', undefined, '29rem'] : '100%',
          })}
        >
          {'logo' in church && church.logo && (
            <Image
              priority
              alt="Church Logo"
              height={100}
              src={process.env.NEXT_PUBLIC_ASSET_URL + church.logo}
              style={{
                height: 'auto',
                maxWidth: '100%',
              }}
              width={290}
            />
          )}
          {!church.logo && (
            <h1
              css={mq({
                display: '-webkit-box',
                fontSize: [
                  'clamp(1.6rem, 6vw, 2.4rem)',
                  undefined,
                  'clamp(2rem, 3.5vw, 4rem)',
                ],
                hyphens: 'auto',
                lineHeight: '1.15em',
                margin: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                overflowWrap: 'break-word',
                textAlign: 'center',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                wordBreak: 'break-word',
              })}
            >
              {church.name}
            </h1>
          )}
        </i>
        {course?.name && (
          <h1 css={{ fontSize: '1.8rem', margin: '0 auto 2rem' }}>
            {course?.name}
          </h1>
        )}

        <MaxWidthStyled
          flex
          colGap={0.5}
          css={{ margin: '0 -0.4rem', width: 'auto' }}
          justify="space-between"
        >
          {course?.sections
            .filter(s => !s.parentId)
            .map(s => (
              <TooltipStyled
                key={s.id}
                css={{ display: 'block !important', flex: 1 }}
                position={'bottom'}
                size="big"
                title={s.name}
              >
                <ProgressBarStyled
                  as={Link}
                  barColor={church.buttonColor as ColorInput}
                  complete={(s.order ?? 0) <= (currentSection?.order ?? 0)}
                  href={`/${church.url}/${course.url}/${s.id}`}
                />
              </TooltipStyled>
            ))}
        </MaxWidthStyled>
      </MaxWidthStyled>
    </>
  );
}

export default GuestNav;
